import './App.css';

import '@sendbird/uikit-react/dist/index.css';
import { ThemeProvider } from '@emotion/react';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import * as CryptoJS from 'crypto-js';
import * as locale from 'date-fns/locale';
import { Suspense, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { loginToDashboardApi } from '@/apis/auth';
import { clientOption } from '@/apis/option';
import { userProfileApi } from '@/apis/user';
import ViewWhenNotPcSize from '@/components/common/ViewWhenNotPcSize';
import OutOfChatModalPortal from '@/components/modal/OutOfChatModalPortal';
import { KEY } from '@/consts/keys';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useMatchMedia } from '@/hooks/useMatchMedia';
import strings from '@/i18n/index';
import type { LanguageCodes } from '@/i18n/index';
import ChatMainPage from '@/pages/chat/ChatMainPage';
import NotFound404Page from '@/pages/error/NotFound404Page';
import LoginPage from '@/pages/login/LoginPage';
import useAuth from '@/stores/authStore';
import useChannelListQueryStore from '@/stores/channelListQueryStore';
import useLanguageCodeStore from '@/stores/languageCodeStore';
import useMessageStore from '@/stores/messageStore';
import useTheme from '@/stores/themeStore';
import useThemeStore from '@/stores/themeStore';
import { darkTheme, lightTheme } from '@/styles/theme';

import { ShoplColorSet } from './consts/ShoplColorSet';

type LocalKeys = keyof typeof locale;

// 진입 시점에 확인해야 하는 사항
// 1. 대시보드를 통해 들어온 경우, userId 존재
// 2. 구성원을 통해 들어온 경우, userId, lang, targetUserId
// 3. userId, lang, targetUserId

const App = () => {
  const { userInfo, saveUser, logout } = useAuth();
  const { theme } = useThemeStore();
  // const { connect, sendbirdChat } = useSendbirdApi();
  const { setUserIds } = useChannelListQueryStore();
  const [isLoading, setIsLoading] = useState(true);
  const themeStore = useTheme();
  const { changeLanguageCode, code } = useLanguageCodeStore();
  const navigate = useNavigate();
  const isMobile = useMatchMedia();
  const { clearAll } = useMessageStore();
  const { showServerError } = useErrorHandler();

  const init = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('id');
      const targetLanguage = params.get('lang');
      const targetUserId = params.get('inviteUserId') ?? '';
      // 대시보드에서 'open=setting-modal'로 진입한 경우, 로그인 완료 후 /chat 진입 시점에 설정 모달을 연다.
      const openTarget = params.get('open');

      // MARK 기존 로컬스토리지에 있는 유저와 동일하지 않을 시 로그아웃 후 로그인
      const loginAuthInfo =
        localStorage.getItem(KEY.LOGIN_AUTH) && JSON.parse(localStorage.getItem(KEY.LOGIN_AUTH) || '');

      if (loginAuthInfo && loginAuthInfo?.userInfo) {
        if (userId !== loginAuthInfo?.userInfo?.userId) {
          clearAll();
          logout();
          navigate('/login', {
            replace: true,
          });
          return;
        }
      }

      const lanCode = localStorage.getItem(KEY.LANGUAGE) as LanguageCodes | undefined;

      strings.setLanguage(lanCode || 'ko');
      changeLanguageCode(lanCode || 'ko');

      if (!userId && !userInfo) {
        navigate('/login', {
          replace: true,
        });
        return;
      }

      if (targetLanguage) {
        strings.setLanguage(targetLanguage || 'ko');
        changeLanguageCode((targetLanguage as LanguageCodes) || 'ko');
      }

      if (targetUserId) {
        sessionStorage.setItem('targetInviteUserId', targetUserId);
        setUserIds(targetUserId);
      }

      setIsLoading(true);

      const response = await loginToDashboardApi(userId || userInfo?.userId || '');

      if (response.data.result === '20000') {
        const clientResponse = await clientOption();
        localStorage.setItem(
          KEY.API_ID,
          CryptoJS.AES.encrypt(clientResponse.data?.body?.chatKey, clientResponse.data?.body?.clientId).toString(),
        );
        localStorage.setItem(KEY.CLIENT_ID, clientResponse.data?.body?.clientId);

        const userInfoRes = await userProfileApi();
        const { result: userResult, body } = userInfoRes.data;
        if (userResult === '20000') {
          const userInfo = body;

          if (userInfo) {
            saveUser(userInfo);

            // await connect(userInfo.userId);
            // await sendbirdChat.updateCurrentUserInfo({
            //   profileUrl: `${import.meta.env.VITE_APP_API_URL}${userInfo?.userRegImg}` || '',
            //   nickname: userInfo.userName,
            // });

            // 모달 오픈은 ChatMainPage가 채팅 UI(activeChannelUrl) 준비 후 처리한다.
            // 의도는 open 쿼리 파라미터로 유지하고, 모달 노출 후 ChatMainPage가 파라미터를 제거한다.
            navigate(openTarget === 'setting-modal' ? '/chat?open=setting-modal' : '/chat', {
              replace: true,
            });
          }
        }
      }
    } catch (e) {
      // MARK : 전역 error 핸들러 작업 후, 아래에 넣어주세요.
      showServerError('outOfChat');
    } finally {
      setIsLoading(false);
    }
  };

  const selectedLocale = locale[code as LocalKeys];

  useEffect(() => {
    init();
  }, []);

  const routeMain = () => {
    return (
      <Routes>
        {userInfo ? (
          <>
            <Route path={`/chat/*`} element={<ChatMainPage />} />
            <Route path='*' element={<NotFound404Page />} />
          </>
        ) : (
          <Route path={`*`} element={<Navigate to={`/login`} replace />} />
        )}
      </Routes>
    );
  };

  const userId = userInfo?.userId || 'default';

  // MARK : sendbird의 Loader type이 잘못 명시되어 있어 사용 시 에러가 납니다 (children이 필수값으로 명시되어 있음). Loading component 작업 후, 아래에 넣어주세요.
  if (isLoading) {
    return <div></div>;
  }

  return (
    <SendbirdProvider
      key={`${userId}-${import.meta.env.VITE_APP_ID}`}
      appId={import.meta.env.VITE_APP_ID}
      // userId가 없을 시 default 값으로 설정해줌 (빈 스트링일 경우 에러 발생함)
      userId={userId}
      theme={theme}
      colorSet={ShoplColorSet}
      stringSet={strings.SENDBIRD}
      dateLocale={selectedLocale}
      uikitOptions={{
        groupChannel: {
          enableMention: false,
        },
        groupChannelList: {
          enableTypingIndicator: true,
          enableMessageReceiptStatus: true,
        },
      }}
    >
      <Suspense fallback={<div />}>
        <ThemeProvider
          theme={{
            colors: themeStore.theme === 'dark' ? darkTheme : lightTheme,
          }}
        >
          {isMobile ? (
            <ViewWhenNotPcSize />
          ) : (
            <div className='App'>
              <Routes>
                <Route path='/error/404' element={<NotFound404Page />} />
                {!userInfo && <Route path='/login' element={<LoginPage />} />}
                <Route path='*' element={routeMain()} />
              </Routes>
            </div>
          )}

          <OutOfChatModalPortal />
        </ThemeProvider>
      </Suspense>
    </SendbirdProvider>
  );
};

export default App;
